import { CameraIcon, PencilIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useStockNumber from "../../hooks/useStockNumber";
import { useLabelGenerator } from "../../hooks/useLabelGenerator";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import Modal from "../../layouts/Modal";
import { IVehicle } from "../../models/Vehicle";
import formatDateTime from "../../utils/formatDateTime";
import PriceFormat from "../../utils/priceFormat";
import printHtml from "../../utils/printHtml";
import Card from "../shared/Card/Card";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import VehicleIcons from "../shared/VehicleIcons/VehicleIcons";
import DepollutionButton from "../depollution/DepollutionButton";
import ExportOffersTableRow from "../exports/ExportOffersTableRow";
import PartVehicleSalvage from "../leads/LeadParts/PartVehicleSalvage";
import useNotification from "../notifications/useNotifications";
import VehicleCollectionQuestions from "./VehicleCollectionQuestions";
import VehiclePurchaseVat from "./VehiclePurchaseVat";
import EnginesWorldwideLookup from "./EnginesWorldwideLookup";
import VehicleRecalls from "./VehicleRecalls";
import VehicleStatusCell from "./VehicleStatusCell";

const SingleDetail = ({ title, value, textColour = "text-gray-900" }: { title: string; value: string | null | undefined; textColour?: string; }) => {
  return (
    <div className="py-2 grid grid-cols-3 gap-4">
      <dt className="text-sm font-medium text-gray-500">{title}</dt>
      <dd className={`${textColour} text-sm col-span-2`}>{value}</dd>
    </div>
  );
};

export default function VehicleDetails({
  vehicle,
  driver = false,
  price,
  collectionQuestions = false,
  extendedActions = false,
}: {
  vehicle: IVehicle;
  driver?: boolean;
  /** When provided (incl. null), shows the Price / VAT-Able / icons block. */
  price?: number | null;
  /** Renders the collection-questions checklist at the top. */
  collectionQuestions?: boolean;
  /** Unlocks the PartVehicleDetails-equivalent fields/actions (Series, export values/offers, print label, edit). */
  extendedActions?: boolean;
}) {
  const { t } = useTranslation("vehicles");
  const navigate = useNavigate();
  const { vrmRefresh, update } = useVehiclesMutations();
  const { saveHandlers } = useDefaultCRUDHandlers(t("Vehicle"));
  const { getStockNumber } = useStockNumber();
  const { addNotification } = useNotification();
  const { generateVehicleLabel } = useLabelGenerator();

  const [open, setOpen] = useState(false);
  const [vrm, setVrm] = useState(vehicle.vrm);
  const [exportsOpen, setExportsOpen] = useState(false);
  const [exportValuesOpen, setExportValuesOpen] = useState(false);
  const [ewwEngineOffer, setEwwEngineOffer] = useState(vehicle.enginesWorldwideEngineOffer ?? "");
  const [ewwVfeOffer, setEwwVfeOffer] = useState(vehicle.enginesWorldwideVfeOffer ?? "");
  const [vlaEngineOffer, setVlaEngineOffer] = useState(vehicle.vlaEngineOffer ?? "");
  const [vlaVfeOffer, setVlaVfeOffer] = useState(vehicle.vlaVfeOffer ?? "");
  const [isPrintingVehicleLabel, setIsPrintingVehicleLabel] = useState(false);

  const showPricing = price !== undefined;

  useEffect(() => {
    setVrm(vehicle.vrm);
  }, [vehicle.id, vehicle.vrm]);

  const handleUpdateVrm = () => {
    //Uppercase and remove whitespace from vrm
    const updatedVehicle = {
      ...vehicle,
      vrm: vrm.toUpperCase().replace(/\s/g, ""),
    };

    vrmRefresh.mutate(updatedVehicle, {
      onSuccess: () => {
        setOpen(false);
        saveHandlers.onSuccess();
      },
      onError: () => {
        saveHandlers.onError();
      },
    });
  };

  const openExportValuesModal = () => {
    setEwwEngineOffer(vehicle.enginesWorldwideEngineOffer ?? "");
    setEwwVfeOffer(vehicle.enginesWorldwideVfeOffer ?? "");
    setVlaEngineOffer(vehicle.vlaEngineOffer ?? "");
    setVlaVfeOffer(vehicle.vlaVfeOffer ?? "");
    setExportValuesOpen(true);
  };

  const handleUpdateExportValues = () => {
    const updatedVehicle: IVehicle = {
      ...vehicle,
      enginesWorldwideEngineOffer: ewwEngineOffer || null,
      enginesWorldwideVfeOffer: ewwVfeOffer || null,
      vlaEngineOffer: vlaEngineOffer || null,
      vlaVfeOffer: vlaVfeOffer || null,
    };

    update.mutate(updatedVehicle, {
      onSuccess: () => {
        setExportValuesOpen(false);
        saveHandlers.onSuccess();
      },
      onError: () => {
        saveHandlers.onError();
      },
    });
  };

  const handlePrintVehicleLabel = async () => {
    setIsPrintingVehicleLabel(true);
    try {
      const html = await generateVehicleLabel(vehicle);
      printHtml(html);
    } catch {
      addNotification({
        variant: "error",
        primaryText: t("Unable to print vehicle label"),
      });
    } finally {
      setIsPrintingVehicleLabel(false);
    }
  };

  return (
    <>
      <Modal open={open} setOpen={setOpen} width="max-w-xl">
        <label>{t("Enter VRM to search for:")}</label>
        <input
          value={vrm}
          onChange={(e) => setVrm(e.target.value)}
          className="my-5 w-full text-sm"
          type="text"
        />
        <button
          onClick={() => handleUpdateVrm()}
          className="float-right rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700"
        >
          {t("Save")}
        </button>
      </Modal>

      {extendedActions && (
        <>
          <Modal open={exportsOpen} width={"max-w-2xl"} setOpen={setExportsOpen}>
            <ExportOffersTableRow vehicle={vehicle} />
          </Modal>
          <Modal open={exportValuesOpen} setOpen={setExportValuesOpen} width="max-w-xl">
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">{t("Edit Export Values")}</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t("EWW Engine Offer")}</label>
                <input
                  value={ewwEngineOffer}
                  onChange={(e) => setEwwEngineOffer(e.target.value)}
                  className="mt-1 w-full text-sm"
                  type="number"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t("EWW VFE Offer")}</label>
                <input
                  value={ewwVfeOffer}
                  onChange={(e) => setEwwVfeOffer(e.target.value)}
                  className="mt-1 w-full text-sm"
                  type="number"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t("VLA Engine Offer")}</label>
                <input
                  value={vlaEngineOffer}
                  onChange={(e) => setVlaEngineOffer(e.target.value)}
                  className="mt-1 w-full text-sm"
                  type="number"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t("VLA VFE Offer")}</label>
                <input
                  value={vlaVfeOffer}
                  onChange={(e) => setVlaVfeOffer(e.target.value)}
                  className="mt-1 w-full text-sm"
                  type="number"
                  step="0.01"
                />
              </div>
              <button
                onClick={handleUpdateExportValues}
                className="float-right rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700"
              >
                {t("Save")}
              </button>
            </div>
          </Modal>
        </>
      )}

      <Card
        title={t("Vehicle Details")}
        bodyClassName="p-4"
        collapsible
        headerRight={
          extendedActions ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/vehicles/${vehicle.id}/edit`);
              }}
              className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
              title={t("Edit Vehicle")}
            >
              <PencilIcon className="h-5 w-5" />
            </button>
          ) : undefined
        }
      >
        <dl className="divide-y divide-gray-200">
          {collectionQuestions && (
            <>
              <VehicleCollectionQuestions vehicle={vehicle} />
              <hr className="my-4" />
            </>
          )}

          {!vehicle.found && (
            <div className="mb-4">
              <p className="text-red-400 font-medium text-xl text-center">
                {t("VEHICLE NOT FOUND")}
              </p>

              <div className="flex justify-center w-full mt-2">
                <button
                  onClick={() => {
                    setOpen(true);
                  }}
                  className="rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700"
                >
                  {t("Find Vehicle")}
                </button>
              </div>
            </div>
          )}

          {showPricing && (
            <>
              <SingleDetail title={t("Price")} value={price ? PriceFormat(price) : t("N/A")} />
              {price ? (
                <div className="py-2">
                  <VehicleIcons vehicle={vehicle} hover />
                </div>
              ) : null}
            </>
          )}

          {!driver && (
            <>
              <VehiclePurchaseVat vehicle={vehicle} />
              {vehicle.vatSalesTreatment != null && vehicle.vatSalesTreatment !== 0 && (
                <SingleDetail
                  title={t("Sales VAT")}
                  value={vehicle.vatSalesTreatment === 2 ? t("Margin scheme") : t("Standard")}
                />
              )}
            </>
          )}

          <div className="py-2 grid grid-cols-3 gap-4 items-center">
            <dt className="text-sm font-medium text-gray-500">{t("Status")}</dt>
            <dd className="col-span-2">
              <div className="w-fit">
                <VehicleStatusCell status={vehicle.status} statusDate={vehicle.statusDate} />
              </div>
            </dd>
          </div>

          <SingleDetail
            title={t("Make/Model")}
            value={`${vehicle.combinedMake} ${vehicle.combinedModel}`}
          />

          {vehicle.stockNumber && (
            <SingleDetail title={t("Stock Number")} value={getStockNumber(vehicle)} />
          )}

          {vehicle.originalVrm !== vehicle.vrm && (
            <SingleDetail title={t("Original VRM")} value={vehicle.originalVrm} />
          )}

          <div className="py-2 grid grid-cols-3 gap-4">
            <dt className="text-sm font-medium text-gray-500">{t("VRM")}</dt>
            <dd className="flex justify-between items-center text-sm text-gray-900 col-span-2">
              <div>{vehicle.vrm}</div>
              {!driver && (
                <button
                  onClick={() => setOpen(true)}
                  className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700"
                >
                  {t("Update")}
                </button>
              )}
            </dd>
          </div>

          {vehicle.found && (
            <SingleDetail
              title={t("Year")}
              value={vehicle?.dvlaYearOfManufacture?.toString()}
            />
          )}

          {!driver && vehicle.found && (
            <>
              {extendedActions && (
                <SingleDetail title={t("Series")} value={vehicle?.modelSeries} />
              )}
              <SingleDetail
                title={t("Engine Size")}
                value={`${vehicle?.combinedEngineCapacity}cc`}
              />
              <SingleDetail
                title={t("Body Type")}
                value={`${vehicle?.bodyStyleDescription} - ${vehicle.numberOfDoors} ${t("door")}`}
              />
              <SingleDetail
                title={t("Fuel Type")}
                value={vehicle?.combinedFuelType}
              />
              <SingleDetail
                title={t("Colour Code")}
                value={vehicle?.vinvisColourCode}
              />
              <SingleDetail
                title={t("Transmission Code")}
                value={vehicle?.vinvisTransmissionCode}
              />
              <SingleDetail
                title={t("Transmission")}
                value={`${(vehicle?.combinedForwardGears && vehicle?.combinedForwardGears + " " + t("Speed")) ?? ""} ${vehicle?.combinedTransmission ?? ""}`}
              />
              <SingleDetail
                title={t("Engine Code")}
                value={vehicle?.engineModelCode}
              />
              <SingleDetail title={t("Chassis No")} value={vehicle?.combinedVIN} />
              {vehicle?.insuranceCategory && (
                <SingleDetail
                  title={t("Insurance Category")}
                  value={vehicle.insuranceCategory}
                />
              )}
              <SingleDetail
                title={t("Mileage")}
                value={`${vehicle?.odometerInMiles} ${t("miles")}`}
              />
              {vehicle?.odometerInMiles && vehicle?.dvlaYearOfManufacture && (
                <SingleDetail
                  title={t("Miles/Year")}
                  value={(
                    vehicle?.odometerInMiles /
                    (DateTime.now().year - vehicle?.dvlaYearOfManufacture)
                  )
                    .toFixed(2)
                    .toString()}
                  textColour={
                    vehicle?.odometerInMiles /
                      (DateTime.now().year - vehicle?.dvlaYearOfManufacture) <
                      8000
                      ? "text-green-700 font-bold"
                      : "text-gray-900"
                  }
                />
              )}
              <SingleDetail
                title={t("MOT Expiry")}
                value={formatDateTime(
                  vehicle?.motExpiryDate,
                  DateTime.DATE_SHORT
                )}
              />
              <SingleDetail
                title={t("Year Range")}
                value={`${formatDateTime(vehicle.visibilityDate, {
                  year: "numeric",
                })} - ${formatDateTime(vehicle.terminateDate, {
                  year: "numeric",
                })}`}
              />
              <SingleDetail
                title={t("Kerb Weight")}
                value={
                  vehicle.kerbWeightMin
                    ? `${vehicle.kerbWeightMin} ${vehicle.kerbWeightMax
                      ? ` - ${vehicle.kerbWeightMax}`
                      : ""
                    } kg`
                    : t("Unavailable")
                }
              />
              <SingleDetail
                title={t("Dimensions")}
                value={` ${vehicle.lengthMM ? (vehicle.lengthMM / 1000).toFixed(2) : "0.00"} x ${vehicle.widthMM ? (vehicle.widthMM / 1000).toFixed(2) : "0.00"} x ${vehicle.heightMM ? (vehicle.heightMM / 1000).toFixed(2) : "0.00"} m ${t("(L x W x H)")}`}
              />

              {extendedActions ? (
                <PartVehicleSalvage vehicle={vehicle} />
              ) : (
                <SingleDetail
                  title={t("Salvage Value")}
                  value={PriceFormat(vehicle?.valuationTradePoor)}
                />
              )}

              <SingleDetail
                title={t("Catalytic Converter (Min-Avg-Max)")}
                value={
                  vehicle.catalyticResults !== null &&
                    vehicle.catalyticResults.length > 0
                    ? `£${Math.min(
                      ...vehicle.catalyticResults.map((x) => x.value)
                    )} - £${vehicle.catalyticAveragePrice} - £${Math.max(
                      ...vehicle.catalyticResults.map((x) => x.value)
                    )}`
                    : t("Unavailable")
                }
              />

              {extendedActions ? (
                <>
                  <div onClick={openExportValuesModal} className="hover:bg-gray-100 cursor-pointer rounded-md">
                    <SingleDetail
                      title={t("Export Values")}
                      value={t("VLA Engine: {{engineOffer}} - VLA VFE: {{vfeOffer}}", { engineOffer: PriceFormat(vehicle.vlaEngineOffer), vfeOffer: PriceFormat(vehicle.vlaVfeOffer) })}
                    />
                  </div>
                  <EnginesWorldwideLookup vehicle={vehicle} variant="inline" />
                  <div onClick={() => setExportsOpen(true)} className="hover:bg-gray-100 cursor-pointer rounded-md">
                    <SingleDetail
                      title={t("Export Offers")}
                      value={`${vehicle.exportOffers?.length > 0 ? t("{{count}} Offers", { count: vehicle.exportOffers.length }) : t("No Offers")}`}
                    />
                  </div>
                </>
              ) : (
                <EnginesWorldwideLookup vehicle={vehicle} />
              )}

              <SingleDetail
                title={t("Collected On")}
                value={`${vehicle.collectedOn ? formatDateTime(
                  vehicle.collectedOn,
                  vehicle.collectedOn.includes("T") ? DateTime.DATETIME_SHORT : DateTime.DATE_SHORT
                ) : t("Not Collected")}`}
              />
            </>
          )}

          <div className="py-2 flex flex-wrap items-center gap-2">
            <DepollutionButton vehicleId={vehicle.id} depollutionId={vehicle.depollutionId} openInNewTab />
            {extendedActions && (
              <ButtonInput
                label={t("Print Vehicle Label")}
                isSubmit={false}
                onClick={handlePrintVehicleLabel}
                isLoading={isPrintingVehicleLabel}
                classes=""
              />
            )}
          </div>

          {vehicle.found && <VehicleRecalls vehicle={vehicle} />}

          <div className="py-4 flex items-center justify-center bg-white rounded-lg">
            {vehicle.vehicleImageUrl ? (
              <img className="w-full" src={`${vehicle.vehicleImageUrl}`} alt="" />
            ) : (
              <CameraIcon className="w-1/2 text-gray-600" />
            )}
          </div>
        </dl>
      </Card>
    </>
  );
}
